import {
    CircularProgress,
    Dialog,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import { Package } from "lucide-react";
import {
    getPlanByIdAction,
    createPlanAction,
    updatePlanAction,
} from "../../Actions/PlanManagementActions";
import { MyCloseIcon, MyInfoIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { containsBadWords } from "../../utils/badWord";
import "../../components/Common/FormModal.css";
import "./AddEditPlan.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .matches(/^[a-zA-Z\s]+$/, VALIDATION.INVALID_FORMAT("Plan name"))
        .max(50, VALIDATION.MAX_LENGTH("Plan name", 50))
        .required(VALIDATION.REQUIRED_FIELD("Plan name"))
        .test(
            "bad-word",
            "Plan Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        ),
    description: Yup.string()
        .trim()
        .matches(/^[a-zA-Z\s]+$/, VALIDATION.INVALID_FORMAT("Description"))
        .max(500, VALIDATION.MAX_LENGTH("Description", 500))
        .required(VALIDATION.REQUIRED_FIELD("Description"))
        .test(
            "bad-word",
            "Plan Description contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        ),
    monthly_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Monthly price"))
        .positive(VALIDATION.POSITIVE_ONLY("Monthly price"))
        .max(999999999, VALIDATION.MAX_VALUE("Monthly price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Monthly price")),
    yearly_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Yearly price"))
        .positive(VALIDATION.POSITIVE_ONLY("Yearly price"))
        .max(999999999, VALIDATION.MAX_VALUE("Yearly price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Yearly price")),
    user_limit_type: Yup.string().oneOf(["0", "1"]).required(),
    user_limit_value: Yup.number().when("user_limit_type", {
        is: "0",
        then: (schema) =>
            schema
                .typeError(VALIDATION.NUMERIC_ONLY("User limit"))
                .integer(VALIDATION.NO_DECIMAL("User limit"))
                .min(1, VALIDATION.MIN_VALUE("User limit", 1))
                .max(9999, VALIDATION.MAX_VALUE("User limit", 9999))
                .required(VALIDATION.REQUIRED_FIELD("User limit")),
        otherwise: (schema) => schema.notRequired(),
    }),
    message_limit_type: Yup.string().oneOf(["0", "1"]).required(),
    message_limit_value: Yup.number().when("message_limit_type", {
        is: "0",
        then: (schema) =>
            schema
                .typeError(VALIDATION.NUMERIC_ONLY("Message limit"))
                .integer(VALIDATION.NO_DECIMAL("Message limit"))
                .min(1, VALIDATION.MIN_VALUE("Message limit", 1))
                .max(100000, VALIDATION.MAX_VALUE("Message limit", 100000))
                .required(VALIDATION.REQUIRED_FIELD("Message limit")),
        otherwise: (schema) => schema.notRequired(),
    }),
    agent_training_limit_type: Yup.string().oneOf(["0", "1"]),
    agent_training_limit_value: Yup.number().when("agent_training_limit_type", {
        is: "0",
        then: (schema) =>
            schema
                .typeError(VALIDATION.NUMERIC_ONLY("Agent training limit"))
                .integer(VALIDATION.NO_DECIMAL("Agent training limit"))
                .min(1, VALIDATION.MIN_VALUE("Agent training limit", 1))
                .max(10000, VALIDATION.MAX_VALUE("Agent training limit", 10000))
                .required(VALIDATION.REQUIRED_FIELD("Agent training limit")),
        otherwise: (schema) => schema.notRequired(),
    }),
    isActive: Yup.boolean(),
});

const calculatePlanPricing = (planFeatures, formikValues) => {
    if (!planFeatures || !planFeatures.length) return null;

    const byName = {};
    planFeatures.forEach((f) => {
        byName[f.feature_name] = byName[f.feature_name] || [];
        byName[f.feature_name].push(f);
    });

    const getChild = (parentId, childName) =>
        planFeatures.find(
            (f) => f.parent_id === parentId && f.feature_name === childName
        );

    const getParent = (name) =>
        planFeatures.find((f) => f.feature_name === name && f.level === 1);

    const limits = [
        {
            label: "User Limit",
            typeKey: "user_limit_type",       
            valueKey: "user_limit_value",     
            featureTypeName: "user_limit_type",
            featureValueName: "user_limit_value",
        },
        {
            label: "Message Limit",
            typeKey: "message_limit_type",
            valueKey: "message_limit_value",
            featureTypeName: "message_limit_type",
            featureValueName: "message_limit_value",
        },
        {
            label: "Agent Training Limit",
            typeKey: "agent_training_limit_type",
            valueKey: "agent_training_limit_value",
            featureTypeName: "agent_training_limit_type",
            featureValueName: "agent_training_limit_value",
        },
    ];

    let totalExtraCharge = 0;
    const breakdown = [];

    limits.forEach(({ label, typeKey, valueKey, featureTypeName, featureValueName }) => {
        const typeParent = getParent(featureTypeName);       
        const valueParent = getParent(featureValueName);     
        const isUnlimited = formikValues[typeKey] === "1";

        if (isUnlimited) {
            const unlimitedRow = typeParent
                ? getChild(typeParent.id, "unlimited")
                : null;
            const charge = unlimitedRow ? parseFloat(unlimitedRow.price) : 0;
            totalExtraCharge += charge;
            breakdown.push({
                label,
                type: "Unlimited",
                unitPrice: null,
                quantity: null,
                charge,
            });
        } else {
            const perUnitPrice = valueParent ? parseFloat(valueParent.price) : 0;
            const quantity = parseFloat(formikValues[valueKey]) || 0;
            const charge = perUnitPrice * quantity;
            totalExtraCharge += charge;
            breakdown.push({
                label,
                type: "Limited",
                unitPrice: perUnitPrice,
                quantity,
                charge,
            });
        }
    });

    const baseMonthly = parseFloat(formikValues.monthly_price) || 0;
    const baseYearly = parseFloat(formikValues.yearly_price) || 0;

    return {
        breakdown,
        extraCharge: totalExtraCharge,
        totalMonthly: baseMonthly + totalExtraCharge,
        totalYearly: baseYearly + totalExtraCharge,
    };
};


const getChangedPlanFields = (oldData, newData) => {
    const changes = {};
    if (!oldData) return changes;

    Object.keys(newData).forEach((key) => {
        const oldVal = oldData[key];
        const newVal = newData[key];

        
        const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
        const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

        if (oldIsEmpty && newIsEmpty) return;

        
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes[key] = {
                old: oldVal,
                new: newVal,
            };
        }
    });

    return changes;
};

const AddEditPlan = ({
    open,
    handleClose,
    id,
    refetchPlanList = () => { },
    handleToast = () => { },
    changePortalFilter,
    featureData,
}) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [planData, setPlanData] = useState(null);
    const [features, setFeatures] = useState(null);
    const [changedFields, setChangedFields] = useState({});
    const [extraAddOnCharge, setExtraAddOnCharge] = useState(0);

    const formik = useFormik({
        initialValues: {
            name: "",
            description: "",
            monthly_price: "",
            yearly_price: "",
            user_limit_type: "0",
            user_limit_value: 1,
            message_limit_type: "0",
            message_limit_value: 1,
            agent_training_limit_type: "0",
            agent_training_limit_value: 1,
            isActive: true,
        },
        validationSchema,
        onSubmit: async (values) => {
            values = { ...values, name: values.name?.trim(), description: values.description?.trim() };
            let payload = {
                ...values,
                isExtraCharges: extraAddOnCharge,
                feature_snapshot: pricingData.breakdown,
            }
            setLoading(true);
            try {
                if (id) {
                    const dataToSend = {
                        ...payload,
                        changedFields: JSON.stringify(Object.keys(changedFields)),
                        comparisonData: JSON.stringify(changedFields),
                    };
                    const response = await updatePlanAction(id, dataToSend);
                    const { message } = response?.data || {};
                    handleToast("success", message);
                } else {
                    const response = await createPlanAction(payload);
                    const { message } = response?.data || {};
                    changePortalFilter("qbot");
                    handleToast("success", message);
                }
                handleClose();
            } catch (err) {
                const message = err?.message ?? (id ? "Something went wrong while updating plan" : "Something went wrong while adding plan");
                handleToast("error", message);
                console.error("❌ Error saving plan:", err);
            } finally {
                setLoading(false);
                refetchPlanList();
            }
        },
    });

    useEffect(() => {
        const fetchPlan = async () => {
            if (!id) return;
            setFetching(true);
            try {
                const response = await getPlanByIdAction(id);
                const { data, planFeatures } = response?.data || {};

                setPlanData(data);
                setFeatures(planFeatures);
                formik.resetForm({
                    values: {
                        name: data.name || "",
                        description: data.description || "",
                        monthly_price: data.monthly_price ?? "",
                        yearly_price: data.yearly_price ?? "",
                        user_limit_type:
                            data.user_limit_type !== undefined
                                ? String(data.user_limit_type)
                                : "0",
                        user_limit_value: data.user_limit_value ?? 1,
                        message_limit_type:
                            data.message_limit_type !== undefined
                                ? String(data.message_limit_type)
                                : "0",
                        message_limit_value: data.message_limit_value ?? "",
                        agent_training_limit_type:
                            data.agent_training_limit_type !== undefined
                                ? String(data.agent_training_limit_type)
                                : "0",
                        agent_training_limit_value:
                            data.agent_training_limit_value ?? "",
                        isActive: data.isActive ?? true,
                    },
                });
            } catch (err) {
                console.error("❌ Error fetching plan:", err);
            } finally {
                setFetching(false);
            }
        };

        fetchPlan();
    }, [id]);

    useEffect(() => {
        if (!featureData) return;
        const pricing = calculatePlanPricing(featureData, formik.values);
        if (!pricing) return;
        setExtraAddOnCharge(pricing?.extraCharge || 0);

    }, [featureData, formik.values]);

    useEffect(() => {
        if (!id || !planData) return;

        const oldData = {
            name: planData.name || "",
            description: planData.description || "",
            monthly_price: planData.monthly_price ?? "",
            yearly_price: planData.yearly_price ?? "",
            user_limit_type: String(planData.user_limit_type ?? "0"),
            user_limit_value: planData.user_limit_value ?? 1,
            message_limit_type: String(planData.message_limit_type ?? "0"),
            message_limit_value: planData.message_limit_value ?? "",
            agent_training_limit_type: String(planData.agent_training_limit_type ?? "0"),
            agent_training_limit_value: planData.agent_training_limit_value ?? "",
            isActive: planData.isActive ?? true,
        };

        const changed = getChangedPlanFields(oldData, formik.values);
        setChangedFields(changed);
    }, [formik.values, planData, id]);

    const handleTextChange = (e) => {
        formik.handleChange(e);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleNumberChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        formik.setFieldValue(e.target.name, value);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const getUnlimitedPrice = (featureTypeName) => {
        if (!featureData) return null;
        const parent = featureData.find(
            (f) => f.feature_name === featureTypeName && f.level === 1
        );
        if (!parent) return null;
        const child = featureData.find(
            (f) => f.parent_id === parent.id && f.feature_name === "unlimited"
        );
        return child ? parseFloat(child.price) : null;
    };

    const getLimitedUnitPrice = (featureValueName) => {
        if (!featureData) return null;
        const row = featureData.find(
            (f) => f.feature_name === featureValueName && f.level === 1
        );
        return row ? parseFloat(row.price) : null;
    };

    const getOldCharge = (label) => {
        const item = (planData?.feature_snapshot || []).find(
            (x) => x.label === label
        );

        return item ? Number(item.charge) : null;
    };

    const getPriceForBadge = (price, label) => {
        if (price == null || price <= 0) return null;

        const oldPrice = getOldCharge(label);

        if (
            id &&
            oldPrice != null &&
            Number(oldPrice) !== Number(price)
        ) {
            return {
                changed: true,
                text: `$${oldPrice.toFixed(2)} → $${Number(price).toFixed(2)}`
            };
        }

        return {
            changed: false,
            value: Number(price)
        };
    };

    const PriceBadge = ({ price }) => {
        if (!price || price <= 0) return null;
        return (
            <span className="plan-price-badge">+${price.toFixed(2)}</span>
        );
    };

    const pricingData = calculatePlanPricing(featureData, formik.values) || { breakdown: [], extraCharge: 0 };

    const selected = pricingData.breakdown
        .filter(item => item.charge > 0)
        .map(item => ({
            name: `${item.label} (${item.type})`,
            price: item.charge
        }));

    const totalPrice = pricingData.extraCharge || 0;

    const changedExtraChargeFeatures = pricingData.breakdown.filter(
        (item) => item.charge > 0
    );

    const isExtraChargeUpdated =
        id &&
        planData?.isExtraCharges != null &&
        Number(planData.isExtraCharges) !== Number(totalPrice);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: "fm-dialog plan-dialog" }}
        >
            <div className="fm-header">
                <span className="fm-header-icon" aria-hidden>
                    <Package size={20} />
                </span>
                <div className="fm-header-copy">
                    <h2>
                        {id
                            ? "Update QBot Subscription Plan"
                            : "Create QBot Subscription Plan"}
                    </h2>
                    <p>
                        {id
                            ? "Update plan details, pricing and features."
                            : "Configure pricing, billing and available features."}
                    </p>
                </div>
                <IconButton
                    aria-label="close"
                    className="fm-close"
                    onClick={handleClose}
                >
                    <MyCloseIcon />
                </IconButton>
            </div>

            {fetching ? (
                <div className="plan-loading">
                    <CircularProgress size={32} />
                </div>
            ) : (
                <form onSubmit={formik.handleSubmit} className="fm-form">
                    <div className="fm-body">
                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Plan information</h3>
                                <p>Name and description shown to subscribers.</p>
                            </div>
                            <div className="fm-card">
                                <div className="fm-grid is-stack">
                                    <div className="fm-field">
                                        <label htmlFor="plan-name">
                                            Plan Name
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="plan-name"
                                            placeholder="Enter plan name"
                                            name="name"
                                            size="small"
                                            fullWidth
                                            disabled={id}
                                            value={formik.values.name}
                                            onChange={handleTextChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.name &&
                                                Boolean(formik.errors.name)
                                            }
                                            helperText={
                                                formik.touched.name &&
                                                formik.errors.name
                                            }
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="plan-description">
                                            Description
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="plan-description"
                                            placeholder="Enter description"
                                            name="description"
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={formik.values.description}
                                            onChange={handleTextChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.description &&
                                                Boolean(formik.errors.description)
                                            }
                                            helperText={
                                                formik.touched.description &&
                                                formik.errors.description
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Pricing</h3>
                                <p>Base subscription amounts before add-ons.</p>
                            </div>
                            <div className="fm-card">
                                <div className="fm-grid">
                                    <div className="fm-field">
                                        <label htmlFor="monthly_price">
                                            Monthly Base Price
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="monthly_price"
                                            placeholder="Enter monthly price"
                                            name="monthly_price"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={formik.values.monthly_price}
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.monthly_price &&
                                                Boolean(formik.errors.monthly_price)
                                            }
                                            helperText={
                                                formik.touched.monthly_price &&
                                                formik.errors.monthly_price
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <span className="plan-currency">
                                                        $
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </div>
                                    <div className="fm-field">
                                        <label htmlFor="yearly_price">
                                            Yearly Base Price
                                            <span className="fm-req">*</span>
                                        </label>
                                        <TextField
                                            id="yearly_price"
                                            placeholder="Enter yearly price"
                                            name="yearly_price"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={formik.values.yearly_price}
                                            onChange={handleNumberChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.yearly_price &&
                                                Boolean(formik.errors.yearly_price)
                                            }
                                            helperText={
                                                formik.touched.yearly_price &&
                                                formik.errors.yearly_price
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <span className="plan-currency">
                                                        $
                                                    </span>
                                                ),
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Usage limits</h3>
                                <p>
                                    Cap users, messages, and agent training for this
                                    plan.
                                </p>
                            </div>
                            <div className="fm-card plan-limits">
                                {/* User Limit */}
                                <div
                                    className={`plan-limit-row${
                                        formik.values.user_limit_type === "0"
                                            ? " has-value"
                                            : ""
                                    }`}
                                >
                                    <div className="plan-limit-main">
                                        <div className="plan-limit-label">
                                            <span>User Limit</span>
                                            <Tooltip
                                                title="Set the maximum number of users allowed for this plan"
                                                arrow
                                                placement="right"
                                            >
                                                <IconButton
                                                    size="small"
                                                    className="plan-info-btn"
                                                >
                                                    <MyInfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                                {formik.values.user_limit_type === "1" && (() => {
                                                    const price = getUnlimitedPrice("user_limit_type");
                                                    const info = getPriceForBadge(price, "User Limit");
                                                    if (!info) return null;

                                                    if (info.changed) {
                                                        return (
                                                            <span className="plan-price-badge">
                                                                +{info.text}
                                                            </span>
                                                        );
                                                    }
                                                    return <PriceBadge price={info.value} />;
                                                })()}
                                            
                                                {formik.values.user_limit_type === "0" &&
                                                    (() => {
                                                        const price = getLimitedUnitPrice("user_limit_value");

                                                        const info = getPriceForBadge(price, "User Limit");

                                                        if (!info) return null;

                                                        if (info.changed) {
                                                            return (
                                                                <span className="plan-unit-price">
                                                                    {info.text}/user
                                                                </span>
                                                            );
                                                        }

                                                        return (
                                                            <span className="plan-unit-price">
                                                                {info.value.toFixed(2)}/user
                                                            </span>
                                                        );
                                                    })()}
                                        </div>
                                        <RadioGroup
                                            row
                                            name="user_limit_type"
                                            value={formik.values.user_limit_type}
                                            onChange={formik.handleChange}
                                            className="plan-radios"
                                        >
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio size="small" />}
                                                label="Limited"
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio size="small" />}
                                                label="Unlimited"
                                            />
                                        </RadioGroup>
                                    </div>
                                    {formik.values.user_limit_type === "0" && (
                                        <div className="fm-field plan-limit-value">
                                            <label htmlFor="user_limit_value">
                                                Limit Value
                                                <span className="fm-req">*</span>
                                            </label>
                                            <TextField
                                                id="user_limit_value"
                                                name="user_limit_value"
                                                type="number"
                                                size="small"
                                                fullWidth
                                                placeholder="Enter user limit"
                                                value={
                                                    formik.values.user_limit_value
                                                }
                                                onChange={handleNumberChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched
                                                        .user_limit_value &&
                                                    Boolean(
                                                        formik.errors
                                                            .user_limit_value
                                                    )
                                                }
                                                helperText={
                                                    formik.touched
                                                        .user_limit_value &&
                                                    formik.errors.user_limit_value
                                                }
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Message Limit */}
                                <div
                                    className={`plan-limit-row${
                                        formik.values.message_limit_type === "0"
                                            ? " has-value"
                                            : ""
                                    }`}
                                >
                                    <div className="plan-limit-main">
                                        <div className="plan-limit-label">
                                            <span>Message Limit</span>
                                            <Tooltip
                                                title="Your message limit will reset every month"
                                                arrow
                                                placement="right"
                                            >
                                                <IconButton
                                                    size="small"
                                                    className="plan-info-btn"
                                                >
                                                    <MyInfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                                {formik.values.message_limit_type === "1" &&
                                                    (() => {
                                                        const price = getUnlimitedPrice("message_limit_type");

                                                        const info = getPriceForBadge(price, "Message Limit");

                                                        if (!info) return null;

                                                        if (info.changed) {
                                                            return (
                                                                <span className="plan-price-badge">
                                                                    +{info.text}
                                                                </span>
                                                            );
                                                        }

                                                        return <PriceBadge price={info.value} />;
                                                    })()}


                                                {formik.values.message_limit_type === "0" &&
                                                    (() => {
                                                        const price = getLimitedUnitPrice("message_limit_value");

                                                        const info = getPriceForBadge(price, "Message Limit");

                                                        if (!info) return null;

                                                        if (info.changed) {
                                                            return (
                                                                <span className="plan-unit-price">
                                                                    {info.text}/message
                                                                </span>
                                                            );
                                                        }

                                                        return (
                                                            <span className="plan-unit-price">
                                                                ${info.value.toFixed(2)}/message
                                                            </span>
                                                        );
                                                    })()}
                                        </div>
                                        <RadioGroup
                                            row
                                            name="message_limit_type"
                                            value={
                                                formik.values.message_limit_type
                                            }
                                            onChange={formik.handleChange}
                                            className="plan-radios"
                                        >
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio size="small" />}
                                                label="Limited"
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio size="small" />}
                                                label="Unlimited"
                                            />
                                        </RadioGroup>
                                    </div>
                                    {formik.values.message_limit_type === "0" && (
                                        <div className="fm-field plan-limit-value">
                                            <label htmlFor="message_limit_value">
                                                Limit Value
                                                <span className="fm-req">*</span>
                                            </label>
                                            <TextField
                                                id="message_limit_value"
                                                name="message_limit_value"
                                                type="number"
                                                size="small"
                                                fullWidth
                                                placeholder="Enter message limit"
                                                value={
                                                    formik.values
                                                        .message_limit_value
                                                }
                                                onChange={handleNumberChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched
                                                        .message_limit_value &&
                                                    Boolean(
                                                        formik.errors
                                                            .message_limit_value
                                                    )
                                                }
                                                helperText={
                                                    formik.touched
                                                        .message_limit_value &&
                                                    formik.errors
                                                        .message_limit_value
                                                }
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Agent Training Limit */}
                                <div
                                    className={`plan-limit-row${
                                        formik.values
                                            .agent_training_limit_type === "0"
                                            ? " has-value"
                                            : ""
                                    }`}
                                >
                                    <div className="plan-limit-main">
                                        <div className="plan-limit-label">
                                            <span>Agent Training Limit</span>
                                            <Tooltip
                                                title="Your agent training limit will reset every month"
                                                arrow
                                                placement="right"
                                            >
                                                <IconButton
                                                    size="small"
                                                    className="plan-info-btn"
                                                >
                                                    <MyInfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                                {formik.values.agent_training_limit_type === "1" &&
                                                    (() => {
                                                        const price = getUnlimitedPrice("agent_training_limit_type");

                                                        const info = getPriceForBadge(
                                                            price,
                                                            "Agent Training Limit"
                                                        );

                                                        if (!info) return null;

                                                        if (info.changed) {
                                                            return (
                                                                <span className="plan-price-badge">
                                                                    +{info.text}
                                                                </span>
                                                            );
                                                        }

                                                        return <PriceBadge price={info.value} />;
                                                    })()}


                                                {formik.values.agent_training_limit_type === "0" &&
                                                    (() => {
                                                        const price = getLimitedUnitPrice(
                                                            "agent_training_limit_value"
                                                        );

                                                        const info = getPriceForBadge(
                                                            price,
                                                            "Agent Training Limit"
                                                        );

                                                        if (!info) return null;

                                                        if (info.changed) {
                                                            return (
                                                                <span className="plan-unit-price">
                                                                    {info.text}/agent
                                                                </span>
                                                            );
                                                        }

                                                        return (
                                                            <span className="plan-unit-price">
                                                                ${info.value.toFixed(2)}/agent
                                                            </span>
                                                        );
                                                    })()}
                                        </div>
                                        <RadioGroup
                                            row
                                            name="agent_training_limit_type"
                                            value={
                                                formik.values
                                                    .agent_training_limit_type
                                            }
                                            onChange={formik.handleChange}
                                            className="plan-radios"
                                        >
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio size="small" />}
                                                label="Limited"
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio size="small" />}
                                                label="Unlimited"
                                            />
                                        </RadioGroup>
                                    </div>
                                    {formik.values.agent_training_limit_type ===
                                        "0" && (
                                        <div className="fm-field plan-limit-value">
                                            <label htmlFor="agent_training_limit_value">
                                                Limit Value
                                                <span className="fm-req">*</span>
                                            </label>
                                            <TextField
                                                id="agent_training_limit_value"
                                                name="agent_training_limit_value"
                                                type="number"
                                                size="small"
                                                fullWidth
                                                placeholder="Enter training limit"
                                                value={
                                                    formik.values
                                                        .agent_training_limit_value
                                                }
                                                onChange={handleNumberChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched
                                                        .agent_training_limit_value &&
                                                    Boolean(
                                                        formik.errors
                                                            .agent_training_limit_value
                                                    )
                                                }
                                                helperText={
                                                    formik.touched
                                                        .agent_training_limit_value &&
                                                    formik.errors
                                                        .agent_training_limit_value
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Plan status</h3>
                                <p>Control whether this plan can be subscribed to.</p>
                            </div>
                            <div className="fm-card">
                                <div className="fm-toggle-list">
                                    <label
                                        className={`fm-toggle${
                                            formik.values.isActive ? " is-on" : ""
                                        }`}
                                    >
                                        <span>
                                            <p className="fm-toggle-title">
                                                {formik.values.isActive
                                                    ? "Active"
                                                    : "Inactive"}
                                            </p>
                                            <p className="fm-toggle-sub">
                                                {formik.values.isActive
                                                    ? "This plan is available for users to subscribe"
                                                    : "This plan is hidden from users"}
                                            </p>
                                        </span>
                                        <Switch
                                            checked={formik.values.isActive}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    "isActive",
                                                    e.target.checked
                                                )
                                            }
                                            color="primary"
                                        />
                                    </label>
                                </div>
                            </div>
                        </section>

                            {/* Extra Charges Section */}
                            <div className="plan-charges">

                                <div className="plan-charges-copy">
                                    <span className="plan-charges-icon" aria-hidden>
                                        <Package size={18} />
                                    </span>

                                    <div>
                                        <strong>Extra Charges</strong>

                                        <p>
                                            {isExtraChargeUpdated
                                                ? "This amount will be applicable after the plan is updated"
                                                : "This amount will be charged based on selected limits"}
                                        </p>
                                    </div>
                                </div>


                                <div className="plan-charges-amount-wrapper">

                                    {isExtraChargeUpdated && (
                                        <div className="plan-charges-previous">

                                            <span className="old-price">
                                                Was: $ {Number(planData.isExtraCharges).toFixed(2)}
                                            </span>

                                            <span className="arrow">
                                                →
                                            </span>

                                        </div>
                                    )}
                                    <div className="plan-charges-amount">
                                        $ {totalPrice.toFixed(2)}
                                    </div>
                                </div>

                            </div>
                    </div>

                    <div className="fm-footer">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="fm-btn"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="fm-btn is-primary"
                            disabled={loading}
                        >
                            {id ? "Update" : "Create"} Plan
                            {loading && (
                                <CircularProgress size={16} color="inherit" />
                            )}
                        </button>
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default AddEditPlan;
